import { InjectionToken } from "./Types/InjectionToken";

declare global {
  interface Window {
    __DI_REGISTRY__?: Map<string | symbol, object | number | string | boolean>;
  }
}
/**
 * My interpretation of Dependency Injection in TypeScript
 *
 * Dependency injection projects in Typescript are (mostly) overly complicated and require a lot of unpleasant code.
 * I like TSyringe, but it's even that is really complicated and ugly; they don't approach the simplicity of either
 * zenject or Symfony. This is my attempt to create a simple DI container that is easy to use and understand.
 *
 * All services are instantiated as singletons. We already use singletons/static code frequently, so I think this is an
 * easy rule to enforce.
 *
 * (We could use a factory pattern to create new instances of services if we wanted to, but I don't think it's necessary atm)
 */
export default abstract class Container {
  /**
   * Gets the registry for the container. If it doesn't exist, it creates a new one.
   *
   * We create it on the window object so that it's accessible from everywhere
   * on the page. This is not ideal, but it's one way to make it work.
   *
   * An alternative would be to make sure that every entrypoint uses the same
   * instance of the container, but I'm not sure how to get webpack to do that.
   * @private
   */
  private static get registry(): Map<
    string | symbol,
    object | number | string | boolean
  > {
    if (typeof window === "undefined") {
      throw new Error("Container requires window object");
    }

    if (!window.__DI_REGISTRY__) {
      window.__DI_REGISTRY__ = new Map<
        string | symbol,
        object | number | string | boolean
      >();
    }

    return window.__DI_REGISTRY__;
  }

  /**
   * Gets a service from the container. At the moment services are singletons.
   * @param token should be a string that uniquely identifies the service. It is HIGHLY recommended to use the class or interface name.
   * @param defaultValue The value to return if the token is not found in the registry. If not provided, this function throws an error when the token is not found.
   */
  public static Get<T>(token: InjectionToken<T>, defaultValue?: T): T {
    const value = this.registry.get(token as string | symbol);

    if (value === undefined) {
      if (defaultValue !== undefined) {
        return defaultValue;
      }

      const key = String(token);

      throw new Error(
        `Registry entry with key ${key} not found. All entries must be initialized before they are retrieved. If you're receiving this error, you probably forgot to call Container.Register() or you've got a Circular Dependency issue.`,
      );
    }

    return value as T;
  }

  /**
   * Registers a service with the container. At the moment all services are singletons.
   * @param token The token linked to the registry entry. If you are registering a concrete class, you can use the type
   * itself; this makes the injection clear (e.g. `Container.Register(CFamilyController, new CFamilyController()`).
   * If you are registering an interface or abstract class, you must use a string or symbol to identify the interface
   * (e.g. `Container.Register("IFamilyController", new CFamilyController())`)
   * @param value
   */
  public static Register<T>(
    token: InjectionToken<T>,
    value: object | number | string | boolean,
  ): void {
    if (this.registry.has(token as string | symbol)) {
      throw new Error(
        `Registry entry with key ${String(token)} already exists. Registry entries must be unique.`,
      );
    }

    this.registry.set(token as string | symbol, value);
  }
}
